import { useEffect, useState } from 'react';

/**
 * The current year, taken from the server's render and recomputed in the browser.
 *
 * Anywhere a year is printed, the value baked into server-rendered markup can be stale:
 * `Full` renders the footer, so on every page that names a layout the footer sits inside
 * that page's own SSR'd tree and is cached with it (a day for the home page, for
 * `/copyright`, and for the institutional pages). From January 1st the cached markup
 * carries the old year until the entry lapses.
 *
 * Which is why the year is *passed in* rather than read from the clock during render.
 * The server puts it in the props, the browser hydrates from those same props, so the
 * client's first render agrees with the markup it is hydrating — no mismatch, and no
 * repaint of a page that was server-rendered precisely to avoid one. The effect then
 * corrects the year after that first render, which is what covers a payload cached from
 * last year and a tab that was open over midnight.
 *
 * What the server does *not* do is key its caches on the date. A props builder emits the
 * year and leaves it out of its cache key, so nothing downstream inherits a key it has to
 * remember — the correction below is the mechanism, and up to a day of staleness before
 * the browser renders is the accepted cost.
 *
 * `initialYear` is optional because not every caller has one: the legacy
 * `layouts/full/footer.html` mount passes no layout props and is `SsrOptions::disabled()`,
 * so it renders in the browser where the clock is the right answer and no cached markup
 * can disagree with it.
 *
 * Deliberately *not* a module-level constant. The SSR bundle is loaded once when the Node
 * process boots and stays resident for weeks, so a constant would freeze the year at
 * process-start and go stale even on an uncached render.
 */
export default function useCurrentYear(initialYear?: number): number {
  const [year, setYear] = useState(() => initialYear ?? new Date().getFullYear());

  useEffect(() => {
    setYear(new Date().getFullYear());
  }, []);

  return year;
}
